/* eslint-disable @typescript-eslint/no-explicit-any */
import { createContext, useContext, useState } from 'react';
import {
  Roles,
  TrackingFamily,
  EntityStatus,
  StepsFilter,
  FlagsProvider,
  AnonymizeProvider,
  CommonProvider,
  Tenant,
} from '@facephi/inphinite-common';
import { ToastProvider } from '@facephi/ui-react';
import { useLocation } from 'react-router-dom';
import { ApiProvider } from './ApiProvider';
import { TranslationProvider } from './TranslationProvider';
import { FiltersType } from '../components';
import { operationsSearch } from '../state/cache';
import { RoutesName, OrderBy } from '../state/constants';

type Props = {
  children: React.ReactNode;
  language: string;
  apiUrl: string;
  token?: string;
  wsUrl?: string;
  role?: Roles;
  timezone?: string;
  onUserOperation?: (id: string) => void;
  showDownload?: boolean;
  tenant?: any;
  activeFlags?: string[];
};

type ContextProps = {
  apiUrl: string;
  token?: string;
  language?: string;
  wsUrl?: string;
  role?: Roles;
  filter?: FilterType;
  setFilter(value: any): void;
  variables?: FiltersType;
  setVariables(value: any): void;
  onUserOperation?: (id: string) => void;
  timezone?: string;
  initialFilter?: FilterType;
  showDownload?: boolean;
  tenant?: Tenant;
  activeFlags?: string[];
};

const Ctx = createContext<ContextProps>({
  apiUrl: '',
  token: '',
  language: '',
  wsUrl: '',
  setFilter: () => {},
  setVariables: () => {},
  timezone: '',
  showDownload: false,
  tenant: { id: '', name: '' },
  activeFlags: [],
});

const options = {
  family: Object.values(TrackingFamily).map((value: string) => {
    return { label: value };
  }),
  status: Object.values(EntityStatus).map((value: string) => ({
    label: value,
  })),
  currentStep: Object.values(StepsFilter).map((value: string) => ({
    label: value,
  })),
};

type FilterType = Record<string, string[] | undefined>;
type State = { from?: string; id?: string };

export const OperationsProvider = ({
  children,
  language,
  apiUrl,
  token,
  wsUrl,
  role,
  timezone,
  onUserOperation,
  showDownload,
  tenant,
  activeFlags,
}: Props) => {
  const location = useLocation();
  const detailRoute = `${RoutesName.operationsList}/${RoutesName.operationsId}`;
  const comesFromDetail = () => {
    const state = location.state as State;

    return state?.from === detailRoute;
  };

  const getSearch = () =>
    comesFromDetail()
      ? operationsSearch()
      : { orderBy: OrderBy.lastUpdateTime };

  const filterElements = getSearch();
  const [variables, setVariables] = useState<FiltersType>(filterElements);

  const initialFilter: FilterType = Object.fromEntries(
    Object.entries(options).map(([header]) => [
      header,
      (filterElements as any)[header] ?? [],
    ]),
  );
  const [filter, setFilter] = useState<FilterType>(initialFilter);

  return (
    <Ctx.Provider
      value={{
        apiUrl,
        token,
        language,
        wsUrl,
        role,
        variables,
        setVariables,
        filter,
        setFilter,
        onUserOperation,
        timezone,
        initialFilter,
        showDownload,
        tenant,
        activeFlags,
      }}
    >
      <TranslationProvider language={language}>
        <CommonProvider tenant={tenant} token={token} role={role as Roles}>
          <ToastProvider>
            <FlagsProvider>
              <AnonymizeProvider>
                <ApiProvider>{children}</ApiProvider>
              </AnonymizeProvider>
            </FlagsProvider>
          </ToastProvider>
        </CommonProvider>
      </TranslationProvider>
    </Ctx.Provider>
  );
};

export const useOperations = () => useContext(Ctx);
